import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Scale } from "lucide-react";
import { analysesApi } from "@/lib/api";
import KpiCard from "@/components/KpiCard";

export default function VitalisteAnalysis() {
  const { projectId } = useParams();
  const [vit, setVit] = useState(null);
  const [project, setProject] = useState(null);

  useEffect(() => {
    analysesApi.summary(projectId).then((d) => { setVit(d.vitaliste); setProject(d.project); });
  }, [projectId]);

  if (!vit) return <div className="p-10 label-mono text-xs text-muted-foreground">Chargement…</div>;

  return (
    <div className="px-6 lg:px-10 py-8 max-w-6xl mx-auto w-full" data-testid="vitaliste-page">
      <div className="label-mono text-[10px] text-gold">ANALYSE VITALISTE — A. ELY MUSTAPHA</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">La dette en vies humaines</h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Toutes les formules sont déroulées pas à pas avec les valeurs exactes extraites et paramètres du projet.
        Aucun appel à une IA — calculs déterministes.
      </p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard testId="vit-d1" label="D1 — DETTE / CONTRIB." value={vit.D1} unit={project?.currency} format="money" />
        <KpiCard testId="vit-d2" label="D2 — JOURS / CONTRIB." value={vit.D2} unit="j" format="days" />
        <KpiCard testId="vit-d4" label="D4 — CARRIÈRE ALIÉNÉE" value={vit.D4} unit="%" format="pct" />
        <KpiCard testId="vit-d5" label="D5 — JOURS / AN" value={vit.D5} unit="j" format="days" />
        <KpiCard testId="vit-v1" label="V1 — JOURS FISCAUX" value={vit.V1} unit="j" format="days" />
        <KpiCard testId="vit-v5" label="V5 — TAVD" value={vit.V5} unit="%" band={vit.tavd_band} format="pct" />
        <KpiCard testId="vit-v6" label="V6 — HÉRITAGE ENFANT" value={vit.V6} unit="j" format="days" />
        <KpiCard testId="vit-ipsd" label="IPSD" value={vit.IPSD} unit="%" format="pct" />
      </div>

      <div className="mt-10 space-y-6">
        {FORMULAS(vit, project).map((f) => (
          <FormulaCard key={f.code} {...f} />
        ))}
      </div>

      <div className="mt-10 border-l-4 border-gold bg-gold/5 p-6 rounded-sm">
        <div className="flex items-start gap-3">
          <Scale size={20} className="text-gold mt-1" />
          <div>
            <div className="font-serif font-bold">Avertissement méthodologique</div>
            <p className="mt-2 text-xs text-muted-foreground">
              Les formules ci-dessus sont dérivées de la <em>Théorie Nouvelle des Finances Publiques</em> (Ahmed ELY Mustapha,
              Bookedition Paris). Les valeurs calculées dépendent de la qualité des extractions documentaires et des
              paramètres vitalistes saisis à la création du projet (Sj, population active, contribuables).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormulaCard({ code, title, expression, vars, result, unit, explanation, interpretation }) {
  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid={`formula-${code.toLowerCase()}`}>
      <div className="bg-marine/5 dark:bg-gold/5 border-b border-border px-6 py-3 flex justify-between items-center">
        <div>
          <div className="label-mono text-[10px] text-marine dark:text-gold">FORMULE {code}</div>
          <div className="font-serif font-bold">{title}</div>
        </div>
        <div className="font-mono text-2xl tabular-nums">
          {result == null ? "—" : (typeof result === "number" ? result.toFixed(2) : result)}
          {unit && <span className="text-xs text-muted-foreground ml-1">{unit}</span>}
        </div>
      </div>
      <div className="p-6">
        <div className="font-mono text-sm bg-background border border-border p-4 rounded-sm overflow-x-auto">
          {expression}
        </div>
        {vars?.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {vars.map((v) => (
              <div key={v.k} className="border border-border rounded-sm p-2 bg-muted/20">
                <div className="label-mono text-[9px] text-muted-foreground">{v.k}</div>
                <div className="font-mono text-xs mt-0.5 tabular-nums">
                  {v.value == null ? "—" : typeof v.value === "number" ? v.value.toLocaleString("fr-FR") : v.value}
                </div>
                {v.desc && <div className="text-[10px] text-muted-foreground mt-0.5">{v.desc}</div>}
              </div>
            ))}
          </div>
        )}
        {explanation && <p className="mt-4 text-xs text-muted-foreground">{explanation}</p>}
        {interpretation && (
          <div className="mt-4 border-l-2 border-gold pl-4 text-sm italic">{interpretation}</div>
        )}
      </div>
    </div>
  );
}

function FORMULAS(v, p) {
  const Sj = p?.daily_wage, POP = p?.active_population, TAX = p?.taxpayers;
  return [
    {
      code: "D1", title: "Dette par contribuable actif",
      expression: `D1 = Dette publique totale / Nombre de contribuables actifs`,
      vars: [
        { k: "Dette totale", value: v.D1 && TAX ? v.D1 * TAX : null, desc: "extrait des documents" },
        { k: "Contribuables", value: TAX, desc: "paramètre projet" },
      ],
      result: v.D1, unit: p?.currency,
      interpretation: v.D1 ? `Chaque contribuable actif en ${p.country} porte l'équivalent de ${formatMoney(v.D1)} ${p.currency} de dette publique.` : "Valeur non calculable (données manquantes).",
    },
    {
      code: "D2", title: "Poids vitaliste de la dette individuelle",
      expression: `D2 = D1 / Sj  [jours de vie hypothéqués par contribuable]`,
      vars: [{ k: "D1", value: v.D1 }, { k: "Sj", value: Sj, desc: "salaire journalier" }],
      result: v.D2, unit: "j",
      interpretation: v.D2 ? `Chaque contribuable hypothèque ${Math.round(v.D2)} jours de sa vie pour rembourser la dette existante.` : null,
    },
    {
      code: "D4", title: "Part de carrière aliénée à la dette",
      expression: `D4 = (D2 / 365) / 40 × 100`,
      vars: [{ k: "D2", value: v.D2 }],
      result: v.D4, unit: "%",
      interpretation: v.D4 ? `${v.D4.toFixed(1)}% d'une carrière de 40 ans est aliénée au seul remboursement de la dette.` : null,
    },
    {
      code: "D5", title: "Service annuel de la dette en vies",
      expression: `D5 = (Service annuel / Pop. active) / Sj`,
      vars: [{ k: "Pop. active", value: POP }, { k: "Sj", value: Sj }],
      result: v.D5, unit: "j/an",
      interpretation: v.D5 ? `Chaque année, ${Math.round(v.D5)} jours de vie sont absorbés par le service de la dette.` : null,
    },
    {
      code: "V5", title: "Taux d'Aliénation Vitaliste (TAVD)",
      expression: `V5 = (D2 / V1) × 100`,
      vars: [{ k: "D2", value: v.D2 }, { k: "V1", value: v.V1, desc: "jours fiscaux" }],
      result: v.V5, unit: "%",
      interpretation: v.tavd_band ? `Niveau ${v.tavd_band.replace("_", " ").toUpperCase()} — seuils : <100% soutenable, 100-200% tension, 200-400% critique, >400% insoutenable.` : null,
    },
    {
      code: "V6", title: "Charge intergénérationnelle — hypothèque sur l'enfant",
      expression: `V6 = (Dette + Déficit projeté 10 ans) / (Pop. active future × Sj futur)`,
      vars: [{ k: "Dette actuelle", value: v.D1 && TAX ? v.D1 * TAX : null }],
      result: v.V6, unit: "j",
      interpretation: v.V6 ? `Un enfant né en ${p.period_end} en ${p.country} hérite de ${Math.round(v.V6)} jours de vie hypothéqués dès sa naissance, sans pouvoir consentir.` : null,
    },
  ];
}

function formatMoney(n) {
  if (!n) return "—";
  if (Math.abs(n) >= 1e9) return (n / 1e9).toFixed(2) + " B";
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(2) + " M";
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + " K";
  return n.toFixed(0);
}
